/**
 * Parse API errors into a consistent { code, message, reason, status } shape.
 * Supports the EnViz standard `{ error: { ... } }` payload and legacy FastAPI `detail`.
 */

const STATUS_CODES = {
  400: "BAD_REQUEST",
  401: "UNAUTHORIZED",
  403: "FORBIDDEN",
  404: "NOT_FOUND",
  422: "VALIDATION_ERROR",
  500: "INTERNAL_ERROR",
  503: "SERVICE_UNAVAILABLE",
};

const DEFAULT_MESSAGES = {
  400: "The request was invalid.",
  401: "Authentication required. Please sign in again.",
  403: "You do not have permission to access this resource.",
  404: "The requested resource was not found.",
  422: "Request validation failed.",
  500: "Something went wrong while processing your request.",
  503: "The service is temporarily unavailable. Please try again later.",
};

const GENERIC_DETAILS = new Set([
  "",
  "internal server error",
  "internal server error.",
  "error",
  "not found",
]);

const normalizeDetail = (detail) => {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (item && typeof item === "object") {
          const loc = Array.isArray(item.loc) ? item.loc.join(".") : "";
          const msg = item.msg || "";
          return loc ? `${loc}: ${msg}` : String(msg);
        }
        return String(item);
      })
      .filter(Boolean)
      .join("; ");
  }
  if (typeof detail === "object") {
    return detail.message || JSON.stringify(detail);
  }
  return String(detail);
};

const isGenericDetail = (detail) =>
  GENERIC_DETAILS.has(String(detail || "").trim().toLowerCase());

const friendlyMessage = (status, detail) => {
  const fallback = DEFAULT_MESSAGES[status] || DEFAULT_MESSAGES[500];
  if (!detail || isGenericDetail(detail)) return fallback;
  return detail;
};

const errorReason = (status, detail, message) => {
  if (detail && !isGenericDetail(detail)) return detail;
  return message;
};

const networkError = (error) => {
  const code = error?.code;
  const messageText = error?.message || "";

  if (code === "ECONNABORTED" || /timeout/i.test(messageText)) {
    return {
      code: "TIMEOUT",
      message: "Request timed out. The server is not responding.",
      reason: "The request exceeded the allowed wait time. Check your connection and try again.",
      status: null,
    };
  }

  if (
    code === "ERR_NETWORK" ||
    code === "ENOTFOUND" ||
    /network error/i.test(messageText) ||
    /unable to reach/i.test(messageText)
  ) {
    return {
      code: "NETWORK_ERROR",
      message: "Unable to reach the server.",
      reason: "Check that the API is running and your network connection is stable.",
      status: null,
    };
  }

  return {
    code: "NETWORK_ERROR",
    message: "Unable to reach the server.",
    reason: messageText || "No response was received from the API.",
    status: null,
  };
};

/**
 * @param {import('axios').AxiosError|Error|any} error
 * @returns {{ code: string, message: string, reason: string, status: number|null }}
 */
export const parseApiError = (error) => {
  const data = error?.response?.data;
  const status = error?.response?.status ?? null;

  if (data?.error && typeof data.error === "object") {
    const { code, message, reason, status: bodyStatus } = data.error;
    return {
      code: code || STATUS_CODES[status] || "ERROR",
      message: message || DEFAULT_MESSAGES[status] || DEFAULT_MESSAGES[500],
      reason: reason || message || DEFAULT_MESSAGES[status] || DEFAULT_MESSAGES[500],
      status: bodyStatus ?? status,
    };
  }

  if (!status) {
    return networkError(error);
  }

  const detail = normalizeDetail(data?.detail ?? data?.message ?? error?.message);
  const message = friendlyMessage(status, detail);
  const reason = errorReason(status, detail, message);

  return {
    code: STATUS_CODES[status] || "ERROR",
    message,
    reason,
    status,
  };
};

export const formatApiErrorText = (error) => {
  const parsed = parseApiError(error);
  if (parsed.reason && parsed.reason !== parsed.message) {
    return `${parsed.message} ${parsed.reason}`;
  }
  return parsed.message;
};

export default parseApiError;
